import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Heart, Sparkles, Phone, Music, Book, MessageSquare, CheckCircle2, Clock, Calendar } from 'lucide-react';

interface SpiritSign {
  id: string;
  loved_one_name: string;
  relationship: string;
  date_of_passing: string;
  requested_sign: string;
  requested_date: string;
  sign_received: boolean;
  sign_received_date: string | null;
  sign_description: string | null;
  confidence_level: number | null;
  sign_category: string;
  emotional_impact: string | null;
  notes: string | null;
}

interface ADCExperience {
  id: string;
  loved_one_name: string;
  experience_date: string;
  experience_type: string;
  description: string;
  confidence_level: number;
  emotional_state_before: string;
  emotional_state_after: string;
  validation_details: string | null;
  is_spontaneous: boolean;
  location: string | null;
}

const signCategories = [
  { value: 'animal', label: 'Animal Visit', examples: 'Butterfly, bird, or other meaningful animal' },
  { value: 'song', label: 'Song/Music', examples: '"Our song" on the radio at meaningful times' },
  { value: 'number', label: 'Number Sequences', examples: '111, 444, their birthday numbers' },
  { value: 'scent', label: 'Scent/Smell', examples: 'Their perfume, cologne, or favourite flower' },
  { value: 'dream', label: 'Dream Visit', examples: 'Vivid dream where they appear' },
  { value: 'object', label: 'Object Movement', examples: 'Finding meaningful objects, lights flickering' },
  { value: 'nature', label: 'Nature Sign', examples: 'Rainbow, cloud formation, specific weather' },
  { value: 'technology', label: 'Technology', examples: 'Phone call, text, photo appearing' },
  { value: 'synchronicity', label: 'Synchronicity', examples: 'Meaningful coincidences' },
  { value: 'feeling', label: 'Presence/Feeling', examples: 'Sensing they are near' }
];

const adcTypes = [
  { value: 'visual', label: 'Visual Appearance', description: 'Seeing their full form or partial appearance' },
  { value: 'voice', label: 'Hearing Their Voice', description: 'Clear audible or internal voice' },
  { value: 'touch', label: 'Physical Touch', description: 'Feeling a touch, hug, or hand on shoulder' },
  { value: 'scent', label: 'Characteristic Scent', description: 'Smelling their perfume, cologne, or tobacco' },
  { value: 'dream', label: 'Dream Visitation', description: 'Ultra-vivid dream that feels real' },
  { value: 'phone', label: 'Phone Contact', description: 'Mysterious calls, texts, or voicemails' },
  { value: 'sync', label: 'Symbolic Synchronicity', description: 'Repeated meaningful coincidences' },
  { value: 'sensing', label: 'Sensing Presence', description: 'Knowing they are there without seeing' },
  { value: 'message', label: 'Message/Sign', description: 'Finding meaningful messages or signs' },
  { value: 'other', label: 'Other Experience', description: 'Other form of contact' }
];

// Component for marking sign as received with proper dialog
function MarkReceivedDialog({ signId, onMarkReceived }: { signId: string, onMarkReceived: (signId: string, description: string, confidence: number) => void }) {
  const [isOpen, setIsOpen] = useState(false);
  const [description, setDescription] = useState('');
  const [confidence, setConfidence] = useState(7);

  const handleSubmit = () => {
    if (!description.trim()) {
      toast.error('Please describe how you received the sign');
      return;
    }
    onMarkReceived(signId, description, confidence);
    setIsOpen(false);
    setDescription('');
    setConfidence(7);
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button
          size="sm"
          variant="outline"
          className="w-full mt-2 border-accent/30 hover:bg-accent/10"
        >
          ✓ Mark as Received
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-card border-accent/20">
        <DialogHeader>
          <DialogTitle>Mark Sign as Received</DialogTitle>
          <DialogDescription>
            Describe how you received this sign and rate your confidence level.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 pt-4">
          <div>
            <Label>Describe how you received this sign:</Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="I saw a monarch butterfly land on my hand while thinking of them..."
              rows={4}
              className="bg-background/80 border-accent/30 focus:border-accent/50"
            />
          </div>
          <div>
            <Label>Confidence Level: {confidence}/10</Label>
            <Slider
              value={[confidence]}
              onValueChange={(value) => setConfidence(value[0])}
              min={1}
              max={10}
              step={1}
              className="mt-2"
            />
            <p className="text-xs text-muted-foreground mt-1">
              How confident are you this was the sign?
            </p>
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setIsOpen(false)}>Cancel</Button>
            <Button onClick={handleSubmit} className="bg-gradient-to-r from-secondary to-accent hover:from-secondary/90 hover:to-accent/90">
              Save
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function SpiritConnectionHub() {
  const [signs, setSigns] = useState<SpiritSign[]>([]);
  const [adcExperiences, setAdcExperiences] = useState<ADCExperience[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('signs');
  
  const [showSignForm, setShowSignForm] = useState(false);
  const [showADCForm, setShowADCForm] = useState(false);
  
  // Filter states
  const [searchName, setSearchName] = useState('');
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<'all' | 'received' | 'waiting'>('all');
  const [customSignCategory, setCustomSignCategory] = useState('');
  const [showCustomCategory, setShowCustomCategory] = useState(false);
  
  // ADC filter states
  const [searchADCName, setSearchADCName] = useState('');
  const [filterADCType, setFilterADCType] = useState<string>('all');
  
  const [signForm, setSignForm] = useState({
    loved_one_name: '',
    relationship: '',
    date_of_passing: '',
    requested_sign: '',
    sign_category: 'animal',
    custom_category: ''
  });

  const [adcForm, setAdcForm] = useState({
    loved_one_name: '',
    experience_type: '',
    description: '',
    confidence_level: 7,
    emotional_state_before: '',
    emotional_state_after: '',
    validation_details: '',
    location: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const [signsResult, adcResult] = await Promise.all([
        supabase.from('spirit_signs').select('*').eq('user_id', user.id).order('created_at', { ascending: false }),
        supabase.from('adc_experiences').select('*').eq('user_id', user.id).order('experience_date', { ascending: false })
      ]);

      if (signsResult.error) throw signsResult.error;
      if (adcResult.error) throw adcResult.error;

      setSigns(signsResult.data || []);
      setAdcExperiences(adcResult.data || []);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load spirit connections');
    } finally {
      setLoading(false);
    }
  };

  // Get unique loved ones and sign categories for filtering
  const uniqueLovedOnes = Array.from(new Set(signs.map(s => s.loved_one_name).filter(Boolean)));
  const uniqueSignCategories = Array.from(new Set(signs.map(s => s.sign_category).filter(Boolean)));
  const uniqueADCLovedOnes = Array.from(new Set(adcExperiences.map(a => a.loved_one_name).filter(Boolean)));
  const uniqueADCTypes = Array.from(new Set(adcExperiences.map(a => a.experience_type).filter(Boolean)));
  
  // Filter signs based on search and filters
  const filteredSigns = signs.filter(sign => {
    const matchesName = searchName === '' || sign.loved_one_name.toLowerCase().includes(searchName.toLowerCase());
    const matchesCategory = filterCategory === 'all' || sign.sign_category === filterCategory;
    const matchesStatus = filterStatus === 'all' || 
      (filterStatus === 'received' && sign.sign_received) ||
      (filterStatus === 'waiting' && !sign.sign_received);
    return matchesName && matchesCategory && matchesStatus;
  });

  // Filter ADC experiences
  const filteredADC = adcExperiences.filter(adc => {
    const matchesName = searchADCName === '' || adc.loved_one_name.toLowerCase().includes(searchADCName.toLowerCase());
    const matchesType = filterADCType === 'all' || adc.experience_type === filterADCType;
    return matchesName && matchesType;
  });

  // Calculate sign success statistics
  const signStats = uniqueSignCategories.map(category => {
    const categorySigns = signs.filter(s => s.sign_category === category);
    const receivedCount = categorySigns.filter(s => s.sign_received).length;
    const totalCount = categorySigns.length;
    const successRate = totalCount > 0 ? Math.round((receivedCount / totalCount) * 100) : 0;
    return { category, receivedCount, totalCount, successRate };
  }).sort((a, b) => b.successRate - a.successRate);

  const handleSignSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error('You must be logged in');
        return;
      }

      // Use custom category if provided, otherwise use selected category
      const categoryToUse = signForm.sign_category === 'custom' && signForm.custom_category 
        ? signForm.custom_category 
        : signForm.sign_category;

      const { error } = await supabase.from('spirit_signs').insert({
        user_id: user.id,
        loved_one_name: signForm.loved_one_name,
        relationship: signForm.relationship,
        date_of_passing: signForm.date_of_passing,
        requested_sign: signForm.requested_sign,
        sign_category: categoryToUse,
        requested_date: new Date().toISOString().split('T')[0]
      });

      if (error) throw error;

      toast.success('Sign request logged! Stay open to receiving it.');
      setShowSignForm(false);
      setSignForm({ loved_one_name: '', relationship: '', date_of_passing: '', requested_sign: '', sign_category: 'animal', custom_category: '' });
      setShowCustomCategory(false);
      loadData();
    } catch (error) {
      console.error('Error saving sign:', error);
      toast.error('Failed to save sign request');
    }
  };

  const markSignReceived = async (signId: string, description: string, confidence: number) => {
    try {
      const { error } = await supabase
        .from('spirit_signs')
        .update({
          sign_received: true,
          sign_received_date: new Date().toISOString().split('T')[0],
          sign_description: description,
          confidence_level: confidence
        })
        .eq('id', signId);

      if (error) throw error;

      toast.success('Sign marked as received!');
      loadData();
    } catch (error) {
      console.error('Error updating sign:', error);
      toast.error('Failed to update sign');
    }
  };

  const handleADCSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error('You must be logged in');
        return;
      }

      const { error } = await supabase.from('adc_experiences').insert({
        user_id: user.id,
        ...adcForm
      });

      if (error) throw error;

      toast.success('Experience logged! You are connected.');
      setShowADCForm(false);
      setAdcForm({
        loved_one_name: '',
        experience_type: '',
        description: '',
        confidence_level: 7,
        emotional_state_before: '',
        emotional_state_after: '',
        validation_details: '',
        location: ''
      });
      loadData();
    } catch (error) {
      console.error('Error saving ADC:', error);
      toast.error('Failed to save experience');
    }
  };

  if (loading) {
    return <div className="text-muted-foreground">Loading spirit connections...</div>;
  }

  return (
    <div className="space-y-6">
      <Card className="p-6 bg-gradient-to-br from-secondary/10 via-accent/5 to-accent/10 border-accent/20">
        <h2 className="text-2xl font-bold mb-2 flex items-center gap-2">
          <Heart className="h-6 w-6 text-accent" />
          Spirit Connection Hub
        </h2>
        <p className="text-muted-foreground mb-4">
          Connect with your loved ones in spirit. Request signs, track after-death communications, and find comfort in continued connection.
        </p>

        <div className="bg-background/50 rounded-lg p-4 space-y-2 mb-4">
          <h3 className="font-semibold flex items-center gap-2">
            <Book className="h-4 w-4" />
            Research-Backed Hope
          </h3>
          <p className="text-sm text-muted-foreground">
            Studies show that 60-90% of bereaved individuals report after-death communications (ADCs). These experiences can provide profound comfort, reduce grief symptoms, and support healing. You're not alone in these experiences - they're a natural part of the grieving process.
          </p>
        </div>
      </Card>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="signs">Request Signs</TabsTrigger>
          <TabsTrigger value="adc">Log ADC Experiences</TabsTrigger>
        </TabsList>

        <TabsContent value="signs" className="space-y-4">
          {/* Search and Filter Section */}
          <Card className="p-4 bg-gradient-to-br from-secondary/5 to-accent/5">
            <div className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs">Search by Name</Label>
                  <Input
                    placeholder="Search loved one's name..."
                    value={searchName}
                    onChange={(e) => setSearchName(e.target.value)}
                    className="bg-background/80 border-accent/30"
                  />
                </div>
                <div>
                  <Label className="text-xs">Filter by Sign Type</Label>
                  <Select value={filterCategory} onValueChange={setFilterCategory}>
                    <SelectTrigger className="bg-background/80 border-accent/30">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-background border-accent/30">
                      <SelectItem value="all" className="focus:bg-accent/20">All Types</SelectItem>
                      {uniqueSignCategories.map(cat => (
                        <SelectItem key={cat} value={cat} className="focus:bg-accent/20">
                          {signCategories.find(sc => sc.value === cat)?.label || cat}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Filter by Status</Label>
                  <Select value={filterStatus} onValueChange={(v) => setFilterStatus(v as any)}>
                    <SelectTrigger className="bg-background/80 border-accent/30">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-background border-accent/30">
                      <SelectItem value="all" className="focus:bg-accent/20">All Signs</SelectItem>
                      <SelectItem value="received" className="focus:bg-accent/20">✓ Received</SelectItem>
                      <SelectItem value="waiting" className="focus:bg-accent/20">⏳ Waiting</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {signStats.length > 0 && (
                <div className="pt-3 border-t border-accent/20">
                  <p className="text-xs font-semibold mb-2">Success Rate by Sign Type:</p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {signStats.slice(0, 4).map(stat => (
                      <div key={stat.category} className="bg-background/50 p-2 rounded text-xs">
                        <p className="font-medium truncate">
                          {signCategories.find(sc => sc.value === stat.category)?.label.split(' ')[0] || stat.category}
                        </p>
                        <p className="text-accent font-bold">{stat.successRate}%</p>
                        <p className="text-muted-foreground">{stat.receivedCount}/{stat.totalCount}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </Card>

          <Card className="p-6">
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold mb-2">How to Request a Sign</h3>
                <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                  <li>Choose a specific, meaningful sign</li>
                  <li>Make it something uncommon but possible</li>
                  <li>Set an intention to be open to receiving it</li>
                  <li>Stay aware and present in your daily life</li>
                  <li>Trust the timing - signs come when you need them</li>
                </ul>
              </div>

              <Button onClick={() => setShowSignForm(!showSignForm)} className="w-full bg-gradient-to-r from-secondary to-accent hover:from-secondary/90 hover:to-accent/90">
                {showSignForm ? 'Cancel' : '✨ Request a New Sign'}
              </Button>

              {showSignForm && (
                <form onSubmit={handleSignSubmit} className="space-y-4 pt-4 border-t">
                  <div>
                    <Label>Loved One's Name *</Label>
                    <Input
                      value={signForm.loved_one_name}
                      onChange={(e) => setSignForm({ ...signForm, loved_one_name: e.target.value })}
                      placeholder="Their name"
                      className="bg-background/80 border-accent/30 focus:border-accent/50"
                      required
                    />
                  </div>

                  <div>
                    <Label>Your Relationship</Label>
                    <Input
                      value={signForm.relationship}
                      onChange={(e) => setSignForm({ ...signForm, relationship: e.target.value })}
                      placeholder="Mother, father, friend, partner..."
                      className="bg-background/80 border-accent/30 focus:border-accent/50"
                    />
                  </div>

                  <div>
                    <Label>Date They Passed (Optional)</Label>
                    <Input
                      type="date"
                      value={signForm.date_of_passing}
                      onChange={(e) => setSignForm({ ...signForm, date_of_passing: e.target.value })}
                      className="bg-background/80 border-accent/30 focus:border-accent/50"
                    />
                  </div>

                  <div>
                    <Label>Sign Category</Label>
                    <Select value={signForm.sign_category} onValueChange={(value) => {
                      setSignForm({ ...signForm, sign_category: value });
                      setShowCustomCategory(value === 'custom');
                    }}>
                      <SelectTrigger className="bg-background/80 border-accent/30">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-background border-accent/30">
                        {signCategories.map(cat => (
                          <SelectItem 
                            key={cat.value} 
                            value={cat.value}
                            className="focus:bg-accent/20 focus:text-foreground data-[state=checked]:bg-accent/10"
                          >
                            <div>
                              <div className="text-foreground">{cat.label}</div>
                              <div className="text-xs text-muted-foreground">{cat.examples}</div>
                            </div>
                          </SelectItem>
                        ))}
                        <SelectItem 
                          value="custom"
                          className="focus:bg-accent/20 focus:text-foreground data-[state=checked]:bg-accent/10"
                        >
                          <div>
                            <div className="text-foreground">✨ Custom Sign Type</div>
                            <div className="text-xs text-muted-foreground">Create your own category</div>
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {showCustomCategory && (
                    <div>
                      <Label>Custom Sign Type Name</Label>
                      <Input
                        value={signForm.custom_category}
                        onChange={(e) => setSignForm({ ...signForm, custom_category: e.target.value })}
                        placeholder="e.g., Feather appearances, Coin finds, etc."
                        className="bg-background/80 border-accent/30 focus:border-accent/50"
                        required={showCustomCategory}
                      />
                    </div>
                  )}

                  <div>
                    <Label>Specific Sign You're Requesting *</Label>
                    <Textarea
                      value={signForm.requested_sign}
                      onChange={(e) => setSignForm({ ...signForm, requested_sign: e.target.value })}
                      placeholder="Be specific: 'A monarch butterfly landing near me' or 'Hearing our song on the radio'"
                      rows={3}
                      className="bg-background/80 border-accent/30 focus:border-accent/50"
                      required
                    />
                  </div>

                  <Button type="submit" className="w-full bg-gradient-to-r from-secondary to-accent hover:from-secondary/90 hover:to-accent/90">Request This Sign</Button>
                </form>
              )}
            </div>
          </Card>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold">Your Requested Signs</h3>
              {filteredSigns.length !== signs.length && (
                <p className="text-sm text-muted-foreground">
                  Showing {filteredSigns.length} of {signs.length} signs
                </p>
              )}
            </div>
            {filteredSigns.length === 0 ? (
              <Card className="p-6 text-center text-muted-foreground">
                <p>
                  {signs.length === 0 
                    ? "No signs requested yet. Start by asking for a sign from a loved one."
                    : "No signs match your current filters."}
                </p>
              </Card>
            ) : (
              filteredSigns.map(sign => (
                <Card key={sign.id} className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <p className="font-medium">{sign.loved_one_name}</p>
                      <p className="text-sm text-muted-foreground">{sign.relationship}</p>
                    </div>
                    {sign.sign_received ? (
                      <Badge className="bg-accent/20 text-accent">
                        <CheckCircle2 className="h-3 w-3 mr-1" />
                        Received
                      </Badge>
                    ) : (
                      <Badge variant="secondary">
                        <Clock className="h-3 w-3 mr-1" />
                        Watching
                      </Badge>
                    )}
                  </div>

                  <div className="space-y-2">
                    <div>
                      <p className="text-xs text-muted-foreground">Requested Sign:</p>
                      <p className="text-sm">{sign.requested_sign}</p>
                    </div>

                    {sign.sign_received && sign.sign_description && (
                      <div className="bg-accent/10 p-3 rounded-lg">
                        <p className="text-xs text-muted-foreground mb-1">
                          Received on {new Date(sign.sign_received_date!).toLocaleDateString()}
                        </p>
                        <p className="text-sm">{sign.sign_description}</p>
                        {sign.confidence_level && (
                          <p className="text-xs text-muted-foreground mt-1">
                            Confidence: {sign.confidence_level}/10
                          </p>
                        )}
                      </div>
                    )}

                    {!sign.sign_received && (
                      <MarkReceivedDialog signId={sign.id} onMarkReceived={markSignReceived} />
                    )}
                  </div>
                </Card>
              ))
            )}
          </div>
        </TabsContent>

        <TabsContent value="adc" className="space-y-4">
          {/* Search and Filter Section for ADC */}
          <Card className="p-4 bg-gradient-to-br from-secondary/5 to-accent/5">
            <div className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Search by Name</Label>
                  <Input
                    placeholder="Search loved one's name..."
                    value={searchADCName}
                    onChange={(e) => setSearchADCName(e.target.value)}
                    className="bg-background/80 border-accent/30"
                  />
                </div>
                <div>
                  <Label className="text-xs">Filter by Experience Type</Label>
                  <Select value={filterADCType} onValueChange={setFilterADCType}>
                    <SelectTrigger className="bg-background/80 border-accent/30">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-background border-accent/30">
                      <SelectItem value="all" className="focus:bg-accent/20">All Types</SelectItem>
                      {uniqueADCTypes.map(type => (
                        <SelectItem key={type} value={type} className="focus:bg-accent/20">
                          {adcTypes.find(t => t.value === type)?.label || type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold mb-2">Types of After-Death Communications</h3>
                <p className="text-sm text-muted-foreground mb-3">
                  ADCs are spontaneous spiritual experiences where you feel contacted by a deceased loved one. Common forms include:
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {adcTypes.slice(0, 6).map(type => (
                    <div key={type.value} className="text-sm p-2 bg-muted/50 rounded">
                      <p className="font-medium">{type.label}</p>
                      <p className="text-xs text-muted-foreground">{type.description}</p>
                    </div>
                  ))}
                </div>
              </div>

              <Button onClick={() => setShowADCForm(!showADCForm)} className="w-full bg-gradient-to-r from-secondary to-accent hover:from-secondary/90 hover:to-accent/90">
                {showADCForm ? 'Cancel' : '💫 Log an ADC Experience'}
              </Button>

              {showADCForm && (
                <form onSubmit={handleADCSubmit} className="space-y-4 pt-4 border-t">
                  <div>
                    <Label>Who Contacted You? *</Label>
                    <Input
                      value={adcForm.loved_one_name}
                      onChange={(e) => setAdcForm({ ...adcForm, loved_one_name: e.target.value })}
                      placeholder="Their name"
                      className="bg-background/80 border-accent/30 focus:border-accent/50"
                      required
                    />
                  </div>

                  <div>
                    <Label>Type of Experience *</Label>
                    <Select value={adcForm.experience_type} onValueChange={(value) => setAdcForm({ ...adcForm, experience_type: value })}>
                      <SelectTrigger className="bg-background/80 border-accent/30">
                        <SelectValue placeholder="Select experience type" />
                      </SelectTrigger>
                      <SelectContent className="bg-background border-accent/30">
                        {adcTypes.map(type => (
                          <SelectItem 
                            key={type.value} 
                            value={type.value}
                            className="focus:bg-accent/20 focus:text-foreground data-[state=checked]:bg-accent/10"
                          >
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Describe the Experience *</Label>
                    <Textarea
                      value={adcForm.description}
                      onChange={(e) => setAdcForm({ ...adcForm, description: e.target.value })}
                      placeholder="What happened? Include as much detail as you can remember..."
                      rows={4}
                      className="bg-background/80 border-accent/30 focus:border-accent/50"
                      required
                    />
                  </div>

                  <div>
                    <Label>Confidence Level: {adcForm.confidence_level}/10</Label>
                    <Slider
                      value={[adcForm.confidence_level]}
                      onValueChange={(value) => setAdcForm({ ...adcForm, confidence_level: value[0] })}
                      max={10}
                      min={1}
                      step={1}
                      className="mt-2"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      How confident are you this was real contact?
                    </p>
                  </div>

                  <div>
                    <Label>How You Felt Before</Label>
                    <Input
                      value={adcForm.emotional_state_before}
                      onChange={(e) => setAdcForm({ ...adcForm, emotional_state_before: e.target.value })}
                      placeholder="Your emotional state before the experience"
                      className="bg-background/80 border-accent/30 focus:border-accent/50"
                    />
                  </div>

                  <div>
                    <Label>How You Felt After</Label>
                    <Input
                      value={adcForm.emotional_state_after}
                      onChange={(e) => setAdcForm({ ...adcForm, emotional_state_after: e.target.value })}
                      placeholder="Your emotional state after the experience"
                      className="bg-background/80 border-accent/30 focus:border-accent/50"
                    />
                  </div>

                  <div>
                    <Label>Validation Details (Optional)</Label>
                    <Textarea
                      value={adcForm.validation_details}
                      onChange={(e) => setAdcForm({ ...adcForm, validation_details: e.target.value })}
                      placeholder="Any validating information that confirmed this was real?"
                      rows={2}
                      className="bg-background/80 border-accent/30 focus:border-accent/50"
                    />
                  </div>

                  <div>
                    <Label>Where Did This Happen?</Label>
                    <Input
                      value={adcForm.location}
                      onChange={(e) => setAdcForm({ ...adcForm, location: e.target.value })}
                      placeholder="Location"
                      className="bg-background/80 border-accent/30 focus:border-accent/50"
                    />
                  </div>

                  <Button type="submit" className="w-full bg-gradient-to-r from-secondary to-accent hover:from-secondary/90 hover:to-accent/90">Save Experience</Button>
                </form>
              )}
            </div>
          </Card>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold">Your ADC Experiences</h3>
              {filteredADC.length !== adcExperiences.length && (
                <p className="text-sm text-muted-foreground">
                  Showing {filteredADC.length} of {adcExperiences.length} experiences
                </p>
              )}
            </div>
            {filteredADC.length === 0 ? (
              <Card className="p-6 text-center text-muted-foreground">
                <p>
                  {adcExperiences.length === 0 
                    ? "No ADC experiences logged yet. When you have one, record it here to track your connections."
                    : "No experiences match your current filters."}
                </p>
              </Card>
            ) : (
              filteredADC.map(exp => {
                const expType = adcTypes.find(t => t.value === exp.experience_type);
                
                return (
                  <Card key={exp.id} className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <p className="font-medium">{exp.loved_one_name}</p>
                        <p className="text-sm text-muted-foreground">{expType?.label}</p>
                      </div>
                      <div className="text-right">
                        <Badge variant="secondary">{exp.confidence_level}/10</Badge>
                        <p className="text-xs text-muted-foreground mt-1">
                          {new Date(exp.experience_date).toLocaleDateString()}
                        </p>
                      </div>
                    </div>

                    <p className="text-sm mb-3">{exp.description}</p>

                    {(exp.emotional_state_before || exp.emotional_state_after) && (
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        {exp.emotional_state_before && (
                          <div className="p-2 bg-muted/50 rounded">
                            <p className="font-medium mb-1">Before:</p>
                            <p className="text-muted-foreground">{exp.emotional_state_before}</p>
                          </div>
                        )}
                        {exp.emotional_state_after && (
                          <div className="p-2 bg-muted/50 rounded">
                            <p className="font-medium mb-1">After:</p>
                            <p className="text-muted-foreground">{exp.emotional_state_after}</p>
                          </div>
                        )}
                      </div>
                    )}

                    {exp.validation_details && (
                      <div className="mt-3 p-2 bg-primary/10 rounded">
                        <p className="text-xs font-medium mb-1">Validation:</p>
                        <p className="text-xs">{exp.validation_details}</p>
                      </div>
                    )}
                  </Card>
                );
              })
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}